import { ChevronRightIcon, CloseIcon, DeleteIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup, MoreActionMenu } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FormEvent, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Node } from 'slate';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';

import { useTasksStore } from '../store/useTasksStore';
import { Task } from '../types/tasks';

export const SubTasks = ({ task }: { task: Partial<Task> & { isLoading?: boolean } }) => {
  const [addingNewTask, setAddingNewTask] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [newTask, setNewTask] = useState('');
  const onAddSubTask = useTasksStore((s) => s.onAddSubTask);
  const onDeleteSubTask = useTasksStore((s) => s.onDeleteSubTask);
  const onReorderSubTask = useTasksStore((s) => s.onReorderSubTask);
  const onMarkAsCompleteSubTask = useTasksStore((s) => s.onMarkAsCompleteSubTask);
  const isFullScreen = useTasksStore((s) => s.isFullScreen);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const boardsStore = useBoardStore();

  const handleMenuItemClick = (subTaskId: string) => (e) => {
    if (e.value === 'delete' && task.id) {
      onDeleteSubTask({ parentTaskId: task.id, taskId: subTaskId });
    }
  };
  const handleAddTask = (e: FormEvent) => {
    e.preventDefault();
    onAddSubTask({ title: newTask, parentTaskId: task.id! });
    setNewTask('');
    inputRef.current?.focus();
  };

  const handleDropSubtask = (result) => {
    if (!result.destination) {
      return;
    }
    onReorderSubTask({
      parentTaskId: task.id!,
      oldIndex: result.source.index,
      newIndex: result.destination.index,
    });
  };

  const handleCloseAddTask = () => {
    setAddingNewTask(false);
    setNewTask('');
  };

  const { isReadOnly } = useTaskAccess(task.id || '');

  const columnOptions = task?.board?.columns || [];

  return (
    <div>
      {task.subtasks && task.subtasks?.length > 0 && (
        <div className="flex items-center gap-8 text-sm text-gray-500">
          <span className="">{!isReadOnly && 'ADD '}SUBTASKS</span>
          <div className="flex-grow border-t" />
        </div>
      )}

      <div className="mb-24">
        <DragDropContext onDragEnd={handleDropSubtask}>
          <Droppable droppableId="subtasks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {task.subtasks?.map((subTask, index) => {
                  const selectedColumn = columnOptions?.find((col) => col._id === subTask?.column);
                  return (
                    <Draggable key={subTask.id} draggableId={subTask.id || ''} index={index}>
                      {(provided) => (
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className="p-6 rounded-sm flex items-center justify-between hover:shadow-sm transition-shadow duration-300"
                        >
                          <div id="left" className="flex items-center flex-grow gap-12">
                            {/* <Checkbox
                            size="small"
                            checked={subTask?.completed}
                            isDisabled={
                              subTask.isLoading ||
                              task.isReadOnly ||
                              task?.access === 'view' ||
                              boardsStore.isPublicView
                            }
                            color="#33CC48"
                            onChange={(e) => {
                              if (task.id && subTask.id) {
                                onMarkAsCompleteSubTask({
                                  parentTaskId: task.id,
                                  taskId: subTask.id,
                                  completed: e,
                                });
                              }
                            }}
                          />{' '} */}
                            <p className="text-sm">
                              {getFormattedValue(subTask.name) || 'Untitled subtask'}
                            </p>
                            {selectedColumn && (
                              <div
                                className="p-4 rounded text-sm"
                                style={{ backgroundColor: selectedColumn.color }}
                              >
                                {selectedColumn.name}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center text-gray-500">
                            {subTask.assignee?.length ? (
                              <div className="flex">
                                <AvatarGroup maxCount={1}>
                                  {subTask.assignee.map((obj) => (
                                    <Avatar
                                      name={cn(obj.firstname, obj.lastname)}
                                      src={obj.avatar}
                                      type="photo"
                                      key={obj.memberId}
                                      wrapperClassName="-mr-8 border-2 border-white"
                                    />
                                  ))}
                                </AvatarGroup>
                                {subTask.assignee.length > 2 && (
                                  <Avatar
                                    name={subTask.assignee.length - 2}
                                    tooltip={false}
                                    type={'initials'}
                                    src={''}
                                    wrapperClassName="-ml-10 border-white border-1 transform transition duration-500 hover:scale-125"
                                  />
                                )}
                              </div>
                            ) : null}
                            {subTask.assignDate?.length ? (
                              <span className={cn('ml-8 mt-2 text-xss text-gray-500 flex', {})}>
                                <span className="leading-22">
                                  {formatDateLight(subTask.assignDate)}
                                </span>
                              </span>
                            ) : null}

                            {!isReadOnly && (
                              <MoreActionMenu
                                usePortal={false}
                                disabled={subTask.isLoading || isReadOnly}
                                onClickMenuItem={
                                  subTask.id ? handleMenuItemClick(subTask.id) : undefined
                                }
                                classNames={{
                                  menuWrap: 'rounded-lg shadow-sm',
                                  labelWrap: 'ml-16 mr-22',
                                }}
                                menuItems={[
                                  {
                                    label: (
                                      <div className="flex font-regular text-gray-999 text-sm ">
                                        <DeleteIcon wrapperClass="mt-4 text-gray-500" />
                                        &nbsp;&nbsp; Delete
                                      </div>
                                    ),
                                    value: 'delete',
                                  },
                                ]}
                              />
                            )}
                            <Link
                              href={{
                                pathname: boardsStore.isPublicView
                                  ? `/${mode}/${orgId}/files/${task.fileId}/public`
                                  : isFullScreen
                                  ? `/${mode}/${orgId}/tasks/${subTask.id}`
                                  : `/${mode}/${orgId}/files/${task.fileId}`,
                                query:
                                  (!isFullScreen && {
                                    view: router.query.view,
                                    type: router.query.type,
                                    taskId: subTask.id,
                                    viewId: router.query.viewId,
                                  }) ||
                                  undefined,
                              }}
                            >
                              <a>
                                <ChevronRightIcon wrapperClass="text-sm px-10 py-5" />
                              </a>
                            </Link>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!isReadOnly && (
          <div className="mt-12">
            {addingNewTask ? (
              <form className="flex" onSubmit={handleAddTask}>
                <input
                  ref={inputRef}
                  className=" border rounded-sm w-330 mr-12 px-12 py-5 text-sm"
                  type="text"
                  value={newTask}
                  onChange={(e) => {
                    setNewTask(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      handleCloseAddTask();
                    }
                    e.stopPropagation();
                  }}
                  placeholder="Untitled subtask"
                  // disabled={addNewTaskSaving}
                  autoFocus
                />
                <button
                  type="submit"
                  className={cn(
                    'bg-gray-200 px-12 text-xs text-gray-700 hover:bg-gray-300 border border-gray-300 rounded-sm mr-6'
                    // {
                    //   ' animate-pulse': addNewTaskSaving,
                    // }
                  )}
                  // disabled={addNewTaskSaving}
                >
                  {
                    // addNewTaskSaving ? 'Saving...' :
                    'Save'
                  }
                </button>
                <button
                  onClick={handleCloseAddTask}
                  // disabled={addNewTaskSaving}
                >
                  <CloseIcon className="ml-5 text-gray-500 text-xss hover:text-gray-600" />
                </button>
              </form>
            ) : (
              <button
                className="flex bg-gray-200 hover:bg-gray-300 border border-gray-300 items-center text-sm text-gray-700 px-8 py-2 rounded-sm"
                tabIndex={-1}
                onClick={() => {
                  setAddingNewTask(true);
                }}
              >
                {/* <AddIcon wrapperClass="text-gray-700 text-sm mr-6" /> */}
                <span className="font-bold text-base mr-6">+</span> Subtask
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function getFormattedValue(value): string {
  if (typeof value === 'string') {
    return value;
  } else if (Array.isArray(value)) {
    return value.map((obj) => Node.string(obj)).join('\n');
  }

  return '';
}

const formatDateLight = (dates) => {
  const dateFormat = 'MMM d, yyyy';
  if (dates.length === 2 && dates?.[0] && dates?.[1]) {
    return `${format(parseISO(dates[0]), dateFormat)} - ${format(parseISO(dates[1]), dateFormat)}`;
  } else if (dates.length === 1 && dates?.[0]) {
    return format(parseISO(dates[0]), dateFormat);
  }
  return '';
};

export const calculatePercentage = (total: number, value: number) =>
  parseFloat(((value / total) * 100).toFixed());
